import { Icon } from "@iconify/react/dist/iconify.js";
import axios from "axios";
import { useEffect, useState, useCallback, useMemo } from "react";
import { Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "./ui/LoadingComponent";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import { TextWrap } from "./ui/TextWrap";
import { usePermissions } from "../context/PermissionContext";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";

const ClientPermissions = () => {
  const [permissions, setPermissions] = useState([]);
  const [filteredPermissions, setFilteredPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    key: "client_permission_id",
    direction: "asc",
  });

  // Modal State for Delete
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const getAuthToken = () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setErrorMessage("Please log in to continue.");
    }
    return token;
  };

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const token = getAuthToken();
      if (!token) {
        setErrorMessage("Authentication token missing. Please log in.");
        setLoading(false);
        return;
      }

      const response = await apiCall(
        "get",
        `/get-all-client-permissions-bo?page=${currentPage}&per_page=${entriesPerPage}&search=${
          searchTerm || ""
        }&sort_by=${sortConfig.key}&sort_order=${sortConfig.direction}`,
        null,
        navigate
      );
      if (response?.data?.success) {
        const permissionList = (response.data.data || []).map((permission) => ({
          ...permission,
          display_created_datetime: permission.created_datetime
            ? new Date(permission.created_datetime).toLocaleDateString(
                "en-GB",
                {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                }
              )
            : "-",
        }));
        setPermissions(permissionList);
        setFilteredPermissions(permissionList);
        setTotalEntries(
          response.data.pagination?.total || permissionList.length
        );
        setTotalPages(response.data.pagination?.last_page || 1);
        handleApiResponse(response.data, "success", "GET");
      } else {
        setErrorMessage(
          response?.data?.message || "Failed to load permissions."
        );
        handleCustomResponse("error", "Failed to load permissions.");
      }
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message ||
          error.message ||
          "Error fetching permissions."
      );
      handleApiResponse(error.response?.data, "Err", "GET");
    } finally {
      setLoading(false);
    }
  };

  const deletePermission = async (id) => {
    try {
      const response = await apiCall(
        "delete",
        `/delete-client-permission/${id}`,
        null,
        navigate
      );

      if (response?.data?.success) {
        fetchPermissions();
        setShowDeleteModal(false);
        handleApiResponse(response.data, "success", "DELETE");
      } else {
        setErrorMessage("Failed to delete permission.");
      }
    } catch (error) {
      handleApiResponse(error.response?.data, "Err", "DELETE");
      setErrorMessage(error.message || "Error deleting permission.");
    }
  };

  const sortData = (key) => {
    let direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const sortedPermissions = useMemo(() => {
    return [...filteredPermissions].sort((a, b) => {
      const valueA = a[sortConfig.key] || "";
      const valueB = b[sortConfig.key] || "";
      if (sortConfig.key === "is_active") {
        const valA = valueA ? "Active" : "Inactive";
        const valB = valueB ? "Active" : "Inactive";
        return sortConfig.direction === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
      if (sortConfig.key === "created_datetime") {
        const dateA = valueA ? new Date(valueA).getTime() : 0;
        const dateB = valueB ? new Date(valueB).getTime() : 0;
        return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
      }
      return sortConfig.direction === "asc"
        ? valueA.toString().localeCompare(valueB.toString())
        : valueB.toString().localeCompare(valueA.toString());
    });
  }, [filteredPermissions, sortConfig]);

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const debouncedFilterPermissions = useCallback(
    debounce((search) => {
      setSearchTerm(search);
      setCurrentPage(1);
    }, 500),
    []
  );

  useEffect(() => {
    fetchPermissions();
  }, [currentPage, entriesPerPage, sortConfig, searchTerm]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedFilterPermissions(value);
  };

  const handleEditClick = (permission) => {
    navigate(`/edit-client-permission/${permission.client_permission_id}`);
  };

  const startIndex =
    totalEntries > 0 ? (currentPage - 1) * entriesPerPage + 1 : 0;
  const endIndex = Math.min(currentPage * entriesPerPage, totalEntries);

  const handleEntriesPerPageChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
    setTotalPages(Math.max(1, Math.ceil(totalEntries / value)));
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };
  if (errorMessage) {
    return <ErrorAlert />;
  }
  return (
    <div className="p-4 p-md-3 p-sm-2">
      <div className="card shadow-sm">
        <div className="card p-3 p-sm-2 p-md-3">
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            <div
              className="icon-field flex-grow-1"
              style={{ maxWidth: "400px" }}
            >
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search by module name..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <span className="icon">
                <Icon icon="ion:search-outline" />
              </span>
            </div>
            <PerPageDropdown
              perPage={entriesPerPage}
              handlePerPageChange={handleEntriesPerPageChange}
            />
            <div className="ms-auto">
              {hasPermission("insert_client_permission") && (
                <Link
                  to="/add-client-permissions"
                  className="btn btn-primary-600 btn-sm d-flex align-items-center"
                >
                  <Icon icon="ri:add-line" className="me-2" /> Add Permission
                </Link>
              )}
            </div>
          </div>

          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show mb-3"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
              ></button>
            </div>
          )}

          {sortedPermissions.length === 0 && !loading ? (
            <TableDataNoAvailable message="No permissions found." />
          ) : (
            <div className="position-relative">
              <div
                className="table-responsive"
                style={{
                  opacity: loading ? 0.4 : 1,
                  pointerEvents: loading ? "none" : "auto",
                }}
              >
                <table className="table bordered-table mb-0 table-hover">
                  <thead>
                    <tr style={{ backgroundColor: "#475EAB", color: "white" }}>
                      <th scope="col" className="p-2">
                        <span>S.No</span>
                      </th>
                      <th
                        scope="col"
                        onClick={() => sortData("module_name")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Module Name</span>
                          <SortIcons
                            field="module_name"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => sortData("permission_key")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Permission Key</span>
                          <SortIcons
                            field="permission_key"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => sortData("description")}
                        className="p-2 d-md-table-cell"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Description</span>
                          <SortIcons
                            field="description"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => sortData("created_by")}
                        className="p-2 d-lg-table-cell"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created By</span>
                          <SortIcons
                            field="created_by"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => sortData("created_datetime")}
                        className="p-2 d-lg-table-cell"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span>Created Date</span>
                          <SortIcons
                            field="created_datetime"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th
                        scope="col"
                        onClick={() => sortData("is_active")}
                        className="p-2"
                        style={{ cursor: "pointer" }}
                      >
                        <div className="d-flex ">
                          <span>Status</span>
                          <SortIcons
                            field="is_active"
                            sortConfig={sortConfig}
                          />
                        </div>
                      </th>
                      <th scope="col" className="p-2">
                        <span>Action</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPermissions.map((permission, index) => (
                      <tr key={permission.client_permission_id || index}>
                        <td className="p-2">{startIndex + index}</td>
                        <td className="p-2">
                          {permission.module_name || "-"}
                        </td>
                        <td className="p-2">
                          {permission.permission_key || "-"}
                        </td>
                        <td className="p-2 d-md-table-cell">
                          <TextWrap
                            location={
                              permission.description || "No description"
                            }
                          />
                        </td>
                        <td className="p-2 d-lg-table-cell">
                          {permission.created_by || "-"}
                        </td>
                        <td className="p-2 d-lg-table-cell">
                          {permission.display_created_datetime}
                        </td>
                        <td className="p-2">
                          <span
                            className={`px-3 py-1 radius-4 fw-medium text-sm ${
                              permission.is_active
                                ? "bg-success-focus text-success-600 border border-success-main"
                                : "bg-danger-focus text-danger-600 border border-danger-main"
                            }`}
                          >
                            {permission.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="p-2">
                          {hasPermission("update_client_permission") && (
                            <button
                              className="w-32-px h-32-px me-2 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                              style={{
                                position: "relative",
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.1)";
                                const tooltip = document.createElement("div");
                                tooltip.textContent = "Edit";
                                tooltip.style.cssText = `
                                position: absolute;
                                top: -35px;
                                left: 50%;
                                transform: translateX(-50%);
                                background-color: #333;
                                color: white;
                                padding: 5px 8px;
                                border-radius: 4px;
                                font-size: 12px;
                                white-space: nowrap;
                                z-index: 1000;
                                pointer-events: none;
                              `;
                                tooltip.id = "edit-tooltip";
                                e.currentTarget.appendChild(tooltip);
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                const tooltip =
                                  document.getElementById("edit-tooltip");
                                if (tooltip) tooltip.remove();
                              }}
                              title="Edit"
                              onClick={() => handleEditClick(permission)}
                            >
                              <Icon icon="lucide:edit" />
                            </button>
                          )}
                          {hasPermission("delete_client_permission") && (
                            <button
                              className="w-32-px h-32-px me-2 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                              style={{
                                position: "relative",
                                transition: "all 0.3s ease",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "scale(1.1)";
                                const tooltip = document.createElement("div");
                                tooltip.textContent = "Delete";
                                tooltip.style.cssText = `
                                position: absolute;
                                top: -35px;
                                left: 50%;
                                transform: translateX(-50%);
                                background-color: #333;
                                color: white;
                                padding: 5px 8px;
                                border-radius: 4px;
                                font-size: 12px;
                                white-space: nowrap;
                                z-index: 1000;
                                pointer-events: none;
                              `;
                                tooltip.id = "edit-tooltip";
                                e.currentTarget.appendChild(tooltip);
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "scale(1)";
                                const tooltip =
                                  document.getElementById("edit-tooltip");
                                if (tooltip) tooltip.remove();
                              }}
                              title="Delete"
                              onClick={() => {
                                setSelectedPermission(permission);
                                setShowDeleteModal(true);
                              }}
                            >
                              <Icon icon="mingcute:delete-2-line" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {loading && (
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                  style={{
                    zIndex: 10,
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <LoadingComponent />
                </div>
              )}
              <PaginationControls
                pagination={{
                  currentPage,
                  perPage: entriesPerPage,
                  total: totalEntries,
                }}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>

      {/* Delete Permission Modal */}
      <div
        className={`modal fade ${showDeleteModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showDeleteModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Delete</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowDeleteModal(false)}
              ></button>
            </div>
            <div className="modal-body">
              Are you sure you want to delete the permission with ID{" "}
              {selectedPermission?.client_permission_id}?
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() =>
                  deletePermission(selectedPermission?.client_permission_id)
                }
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientPermissions;
